import React, { useState } from 'react';

interface ResponsiveImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  aspectRatio?: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  onLoad?: () => void;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

/**
 * High-performance responsive image component
 *
 * Features:
 * - Automatic WebP conversion with fallback
 * - srcset for different device sizes
 * - Lazy loading (unless priority=true)
 * - Aspect ratio for CLS prevention
 * - Loading skeleton
 * - Optimized Unsplash parameters
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  width,
  height,
  aspectRatio,
  className = '',
  sizes = '100vw',
  priority = false,
  onLoad,
  objectFit = 'cover'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Generate optimized image URLs for different sizes
  const generateSrcSet = (baseSrc: string): string => {
    // Check if it's an Unsplash image
    if (baseSrc.includes('unsplash.com')) {
      const widths = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];
      return widths
        .map((w) => {
          // Add Unsplash optimization parameters
          const separator = baseSrc.includes('?') ? '&' : '?';
          return `${baseSrc}${separator}w=${w}&q=80&auto=format&fit=crop ${w}w`;
        })
        .join(', ');
    }

    // For Strapi/local images, return base src
    return `${baseSrc} 1x`;
  };

  // Generate WebP version URL (for Unsplash)
  const getWebPSrc = (baseSrc: string): string | undefined => {
    if (baseSrc.includes('unsplash.com')) {
      const separator = baseSrc.includes('?') ? '&' : '?';
      return `${baseSrc}${separator}fm=webp&q=80&auto=format`;
    }
    return undefined;
  };

  // Calculate aspect ratio from width/height or use provided
  const calculateAspectRatio = (): string => {
    if (aspectRatio) return aspectRatio;
    if (width && height) return `${width}/${height}`;
    return '16/9'; // Default fallback
  };

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const webpSrc = getWebPSrc(src);
  const srcSet = generateSrcSet(src);
  const computedAspectRatio = calculateAspectRatio();

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ aspectRatio: computedAspectRatio }}
    >
      {/* Loading Skeleton */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}

      {/* Error State */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
          <span>Bild nicht verfügbar</span>
        </div>
      )}

      {/* Progressive Image Loading with WebP */}
      {!hasError && (
        <picture>
          {/* WebP format (modern browsers) */}
          {webpSrc && (
            <source
              type="image/webp"
              srcSet={generateSrcSet(webpSrc)}
              sizes={sizes}
            />
          )}

          {/* Fallback JPEG/PNG */}
          <img
            src={src}
            srcSet={srcSet}
            sizes={sizes}
            alt={alt}
            width={width}
            height={height}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'sync' : 'async'}
            onLoad={handleLoad}
            onError={handleError}
            className={`w-full h-full transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              objectFit,
              aspectRatio: computedAspectRatio
            }}
          />
        </picture>
      )}
    </div>
  );
};

export default ResponsiveImage;
